// 兼职数据回传系统 - 前端 API 封装
// 统一走后端 { code, message, data } 结构，code===200 为成功；自动携带 Bearer token。
import { API_CONFIG } from '../config/api';
import { MessagePlugin } from 'tdesign-react';

const BASE = (API_CONFIG && API_CONFIG.BASE_URL) || '/api';

/** 登录已过期处理：清除本地登录态、提示并跳转登录页（替代生硬的"无客服权限"） */
async function handleSessionExpired() {
  localStorage.removeItem('token');
  localStorage.removeItem('isLoggedIn');
  MessagePlugin.error('登录已过期，请重新登录');
  // 短暂停留让提示可见，再整页跳转到登录页
  await new Promise((r) => setTimeout(r, 800));
  window.location.replace('/login');
}

async function req(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data;
  try {
    data = await resp.json();
  } catch (e) {
    throw new Error('响应解析失败');
  }
  if (data && data.code === 401) {
    await handleSessionExpired();
    return undefined; // 页面即将跳转登录页
  }
  if (data && data.code === 200) return data.data;
  throw new Error((data && data.message) || '请求失败');
}

/** 上传（multipart，用于 excel 导入） */
async function upload(path, formData) {
  const token = localStorage.getItem('token');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: formData });
  const data = await resp.json();
  if (data && data.code === 401) {
    await handleSessionExpired();
    return undefined; // 页面即将跳转登录页
  }
  if (data && data.code === 200) return data.data;
  throw new Error((data && data.message) || '上传失败');
}

export const parttimeApi = {
  // ===== 全局配置（后台） =====
  // 批量读取配置值，返回 { key: value }；用于获取各页面分页每页条数
  getConfig: (keys) => req('/parttime/config/get?keys=' + (keys || []).join(',')),

  // ===== 兼职用户（后台） =====
  listUsers: (params) => req(`/parttime/admin/users?` + new URLSearchParams(params || {}).toString()),
  auditUser: (userId, approve, remark) => req('/parttime/admin/audit', {
    method: 'POST', body: { userId, approve, remark },
  }),
  // 批量审核兼职申请（勾选后统一通过/拒绝）
  auditUsers: (userIds, approve, remark) => req('/parttime/admin/audit-batch', {
    method: 'POST', body: { userIds, approve, remark },
  }),
  // 软删除兼职人员（校验其对账完成；相关数据不物理删除）
  deleteUsers: (userIds) => req('/parttime/admin/delete-users', {
    method: 'POST', body: { userIds },
  }),
  resetPassword: (phone, newPassword, confirmPassword) => req('/parttime/admin/reset-password', {
    method: 'POST', body: { phone, newPassword, confirmPassword },
  }),
  // 兼职人员详情（查看弹窗：完整信息+工资版本+时间线）
  userDetail: (userId) => req(`/parttime/admin/user-detail?userId=${userId}`),
  // 兼职人员下拉（选择 ta的团长 用；后端分页，默认每页50条，excludeId 编辑时排除本人）
  userOptions: (params) => req(`/parttime/admin/user-options?` + new URLSearchParams(params || {}).toString()),
  // 新增兼职人员（body 为字段对象）
  createUser: (payload) => req('/parttime/admin/user-create', {
    method: 'POST', body: payload,
  }),
  // 编辑兼职人员（本人登录账号不允许在此修改）
  updateUser: (payload) => req('/parttime/admin/user-update', {
    method: 'POST', body: payload,
  }),
  // 重置账号密码（账号默认回显当前登录账号；密码规则同小程序注册）
  resetUserAccount: (payload) => req('/parttime/admin/user-reset', {
    method: 'POST', body: payload,
  }),
  // 上传 excel 批量导入兼职名单
  importParttimeWorkers: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return upload('/parttime/admin/import-users', fd);
  },

  // ===== 导入（后台） =====
  importPreview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return upload('/parttime/import/preview', fd);
  },
  importConfirm: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return upload('/parttime/import/confirm', fd);
  },
  // 导入进度轮询：{ phase: parsing|saving|done|error, done, total }
  importProgress: () => req('/parttime/import/progress'),
  // 后台上传图片/群二维码（COS）
  uploadToCos: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return upload('/parttime/file/upload', fd);
  },

  // ===== 分配（后台） =====
  workerTree: () => req('/parttime/allocate/worker-tree'),
  pendingData: () => req('/parttime/allocate/pending'),
  // H2 数据分配：搜索分页（业主姓名/手机/微信/小区/城市模糊 + 处理状态）
  allocateSearch: (params) => req('/parttime/allocate/search?' + new URLSearchParams(params || {}).toString()),
  // 修改兼职人员：多兼职按数量逐条绑定（未发送）
  assignWorkers: (dataIds, plan) => req('/parttime/allocate/assign', {
    method: 'POST', body: { dataIds, plan },
  }),
  // 发送给兼职：写单价快照，小程序可见
  sendAssigned: (dataIds) => req('/parttime/allocate/send-assigned', {
    method: 'POST', body: { dataIds },
  }),
  // 物理删除勾选的数据（含分配历史）
  deleteDatas: (dataIds) => req('/parttime/allocate/delete', {
    method: 'POST', body: { dataIds },
  }),
  // 批量修改字段（话术/群二维码/群二维码备注）
  updateLayoutFields: (dataIds, fields) => req('/parttime/allocate/update-fields', {
    method: 'POST', body: Object.assign({ dataIds }, fields),
  }),
  // 撤回数据（回池）
  withdrawDatas: (dataIds) => req('/parttime/allocate/withdraw-datas', {
    method: 'POST', body: { dataIds },
  }),
  // 删除群二维码：置统一文件为已删除状态，并清空数据关联（未分配数据）
  removeQrcode: (dataIds, fileId) => req('/parttime/allocate/remove-qrcode', {
    method: 'POST', body: { dataIds, fileId },
  }),
  // 保存数据群二维码（多张）：body={ dataIds, qrcodes:[{fileId,url}] }
  saveQrcodes: (dataIds, qrcodes) => req('/parttime/allocate/save-qrcodes', {
    method: 'POST', body: { dataIds, qrcodes },
  }),

  // ===== 审核（后台） =====
  auditData: (dataIds, approve, reason) => req('/parttime/review/audit', {
    method: 'POST', body: { dataIds, approve, reason },
  }),
  dataTimeline: (dataId) => req(`/parttime/review/timeline?dataId=${dataId}`),
  // H3 查看：数据完整信息 + 附件图 + 时间线
  dataDetailAdmin: (dataId) => req(`/parttime/review/data-detail?dataId=${dataId}`),
  // H3 数据核对：多条件后端分页列表（业主/兼职/处理/审核/对账/进群时间/对账时间）
  checkList: (params) => req('/parttime/review/check?' + new URLSearchParams(params || {}).toString()),
  // 按勾选数据ID导出（实时生成 Excel→COS），返回 { url, count }
  exportByDataIds: (dataIds) => req('/parttime/export/by-ids', {
    method: 'POST', body: { dataIds },
  }),
  // H5 上半部分「对账结算」按兼职聚合统计（后端分页）：进群时间/审核时间筛选（默认空=全部），只统计审核通过且未对账，返回 { rows, total }
  settleStats: (params) => req('/parttime/review/settle-stats?' + new URLSearchParams(params || {}).toString()),
  // H5 上半部分行内「查看」：某兼职在筛选条件下 审核通过且未对账 的客户数据分页列表
  settlementStatsData: (userId, params) => req('/parttime/review/settlement-stats-data?userId=' + userId + '&' + new URLSearchParams(params || {}).toString()),
  // H5 按勾选的兼职生成对账函：对勾选 userIds 下（审核通过且未对账）按兼职生成本期 settlement，记录筛选条件
  generateByUsers: (userIds, groupStart, groupEnd, reviewStart, reviewEnd) => req('/parttime/review/generate-by-users', {
    method: 'POST', body: { userIds, groupStart, groupEnd, reviewStart, reviewEnd },
  }),
  // H5 生成对账函：对勾选 dataIds（审核通过且未对账）按兼职生成本期 settlement，记录筛选条件
  generateSettlementItems: (dataIds, groupStart, groupEnd, reviewStart, reviewEnd) => req('/parttime/review/generate-settlement', {
    method: 'POST', body: { dataIds, groupStart, groupEnd, reviewStart, reviewEnd },
  }),
  // H5 对账函分页列表（每行一个对账函）：进群时间/审核时间/对账状态筛选，返回 { rows, total }
  settlements: (params) => req('/parttime/review/settlements?' + new URLSearchParams(params || {}).toString()),
  // H5 按筛选条件生成对账函（无需勾选数据）
  generateByFilter: (groupStart, groupEnd, reviewStart, reviewEnd) => req('/parttime/review/generate-by-filter', {
    method: 'POST', body: { groupStart, groupEnd, reviewStart, reviewEnd },
  }),
  // H5 查看对账函及其关联数据
  settlementData: (settlementId) => req(`/parttime/review/settlement-data?settlementId=${settlementId}`),
  // 客服撤回对账函（必填原因）
  withdrawSettlement: (settlementId, reason) => req('/parttime/settlement/withdraw', {
    method: 'POST', body: { settlementId, reason },
  }),
  // H6 全局配置：全量列表 / 保存
  configList: () => req('/parttime/config/list'),
  configSave: (key, value, remark) => req('/parttime/config/save', {
    method: 'POST', body: { key, value, remark },
  }),
  // 进群数据（含各附件附件上传时间）查询：按兼职+日期区间
  groupDataList: (userIds, startDate, endDate) => req('/parttime/export/group-data', {
    method: 'POST', body: { userIds, startDate, endDate },
  }),

  // ===== 对账（后台） =====
  settlementGenerate: (startDate, endDate) => req('/parttime/settlement/generate', {
    method: 'POST', body: { startDate, endDate },
  }),
  settlementSend: (settlementId) => req('/parttime/settlement/send', {
    method: 'POST', body: { settlementId },
  }),
  settlementWithdraw: (settlementId) => req('/parttime/settlement/withdraw', {
    method: 'POST', body: { settlementId },
  }),
  settlementDetail: (settlementId) => req(`/parttime/settlement/detail?settlementId=${settlementId}`),
};